// Halloween 2026 event bundle card — surfaces the $4.99 bundle in the
// Skins page with a live countdown, buy button, and ownership state.
// Renders nothing when the pilot is a guest or the event window has
// closed AND the pilot doesn't own it (past owners get an "Owned"
// keepsake card even after Oct 31 for pride/collection reasons).
import { useEffect, useMemo, useState } from "react";
import { Ghost, ShoppingCart, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";

function timeParts(ms) {
  if (ms <= 0) return { d: 0, h: 0, m: 0, s: 0 };
  const s = Math.floor(ms / 1000);
  return {
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    s: s % 60,
  };
}

export default function HalloweenBundleCard({ compact = false, onPurchased = null }) {
  const [status, setStatus] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/halloween/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const endsMs = useMemo(() => (status ? new Date(status.ends_at).getTime() : 0), [status]);
  const remaining = useMemo(() => Math.max(0, endsMs - now), [endsMs, now]);
  const { d, h, m, s } = timeParts(remaining);

  if (!status) return null;
  // If the event has already ended AND the user doesn't own it, hide.
  if (!status.available && !status.owned) return null;

  const buy = async () => {
    if (busy) return;
    if (!status.has_game_pass) {
      toast.error("Game access required", {
        description: "Start your free trial or unlock the $2.99 game to buy the Halloween bundle.",
      });
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/halloween/checkout", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start Halloween checkout.");
      setBusy(false);
    }
    if (onPurchased) onPurchased();
  };

  const owned = status.owned;
  const gated = !status.has_game_pass;

  return (
    <div
      className={`relative overflow-hidden mb-6 border ${
        owned ? "border-[#ff8a00]/60" : "border-[#ff8a00]"
      } ${compact ? "p-3" : "p-4 sm:p-5"}`}
      style={{
        background:
          "linear-gradient(135deg, rgba(255,106,0,0.18) 0%, rgba(74,0,128,0.28) 55%, rgba(10,0,20,0.9) 100%)",
        boxShadow: owned
          ? "0 0 20px rgba(255,138,0,0.25)"
          : "0 0 26px rgba(255,138,0,0.45), inset 0 0 20px rgba(74,0,128,0.35)",
      }}
      data-testid="halloween-event-card"
    >
      {/* Ghostly grain pattern overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 30%, rgba(255,214,102,0.15) 0px, transparent 40%), radial-gradient(circle at 85% 70%, rgba(255,106,0,0.14) 0px, transparent 45%)",
        }}
      />
      <div className="relative flex flex-wrap items-center gap-4 sm:gap-6">
        <div className="flex items-center gap-3 flex-shrink-0">
          <div
            className="w-11 h-11 sm:w-14 sm:h-14 rounded-full border-2 border-[#ff8a00] flex items-center justify-center"
            style={{ background: "radial-gradient(circle, #ff6a00 0%, #4a0080 80%)", boxShadow: "0 0 18px #ff6a00" }}
          >
            <Ghost className="w-6 h-6 sm:w-8 sm:h-8 text-[#ffe066]" />
          </div>
          <div>
            <div className="font-mono-cosmic text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-[#ffb648]">
              // LIMITED · HALLOWEEN 2026
            </div>
            <div className="font-heading font-black uppercase text-lg sm:text-2xl tracking-tighter text-white">
              Spooky Bundle
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-[220px]">
          <ul className="text-xs sm:text-sm text-white/85 space-y-0.5" data-testid="halloween-bundle-contents">
            <li className="flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#ffd700]" /> Halloween Comet ball skin</li>
            <li className="flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#ffd700]" /> Pumpkin Paddle skin</li>
            <li className="flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#ffd700]" /> Spooky Sound Pack</li>
            <li className="flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#ffd700]" /> Pumpkin Bombs drop from your comet</li>
          </ul>
        </div>

        {!owned && status.available && (
          <div className="flex flex-col items-end gap-1 min-w-[110px]" data-testid="halloween-countdown">
            <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#ffb648]">
              Ends in
            </div>
            <div className="font-heading font-black tabular-nums text-lg sm:text-2xl text-[#ffd700] tracking-tighter leading-none">
              {d}d {String(h).padStart(2, "0")}h {String(m).padStart(2, "0")}m {String(s).padStart(2, "0")}s
            </div>
            <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/50">
              Oct 31 · 23:59 UTC
            </div>
          </div>
        )}

        <div className="flex-shrink-0">
          {owned ? (
            <button
              disabled
              className="inline-flex items-center gap-2 px-5 py-3 border border-[#ffd700] text-[#ffd700] font-mono-cosmic text-[11px] tracking-[0.25em] uppercase"
              data-testid="halloween-owned-badge"
            >
              <Check className="w-4 h-4" /> Owned
            </button>
          ) : gated ? (
            <Link
              to="/unlock"
              className="inline-flex items-center gap-2 px-5 py-3 border border-[#ff8a00]/70 text-[#ffb648] hover:bg-[#ff6a00]/10 font-mono-cosmic text-[11px] tracking-[0.25em] uppercase transition-colors"
              data-testid="halloween-gated-btn"
            >
              Game Access Required · Unlock
            </Link>
          ) : (
            <button
              type="button"
              onClick={buy}
              disabled={busy}
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#ff6a00] text-[#0a0014] font-heading font-black tracking-tight uppercase hover:bg-[#ffb648] transition disabled:opacity-60"
              style={{ boxShadow: "0 0 20px rgba(255,106,0,0.7)" }}
              data-testid="halloween-buy-btn"
            >
              <ShoppingCart className="w-4 h-4" /> Buy · ${status.price_usd.toFixed(2)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
